import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './AdminTable.css';

function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchBookings();
  }, [user, navigate, currentPage, status]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, per_page: 20 };
      if (status) params.status = status;

      const response = await api.get('/api/admin/bookings', { params });
      setBookings(response.data.bookings || []);
      setPagination(response.data.pagination);
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.bookings.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    const reason = prompt(t('admin.bookings.enterCancellationReason'));
    if (!reason) return;

    try {
      await api.post(`/api/admin/bookings/${bookingId}/cancel`, { reason });
      toast.success(t('admin.bookings.cancelSuccess'));
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.bookings.failedToCancel'));
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await api.put(`/api/admin/bookings/${bookingId}/status`, { status: newStatus });
      toast.success(t('admin.bookings.statusUpdated'));
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.bookings.failedToUpdateStatus'));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      confirmed: 'success',
      cancelled: 'danger',
      completed: 'info'
    };
    return colors[status] || 'default';
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>{t('admin.bookings.title')}</h1>
          <p>{t('admin.bookings.subtitle')}</p>
        </div>
      </div>

      <div className="filters">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}>
          <option value="">{t('admin.bookings.allBookings')}</option>
          <option value="pending">{t('admin.bookings.pending')}</option>
          <option value="confirmed">{t('admin.bookings.confirmed')}</option>
          <option value="cancelled">{t('admin.bookings.cancelled')}</option>
          <option value="completed">{t('admin.bookings.completed')}</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">{t('admin.bookings.loading')}</div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{t('admin.bookings.id')}</th>
                  <th>{t('admin.bookings.guest')}</th>
                  <th>{t('admin.bookings.property')}</th>
                  <th>{t('admin.bookings.dates')}</th>
                  <th>{t('admin.bookings.total')}</th>
                  <th>{t('admin.bookings.status')}</th>
                  <th>{t('admin.bookings.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr key={booking.id}>
                    <td>#{booking.id}</td>
                    <td>
                      <div className="user-cell">
                        <div>{booking.guest.name}</div>
                        <div className="sub-text">{booking.guest.email}</div>
                      </div>
                    </td>
                    <td>
                      <div className="property-cell">
                        <div>{booking.property_info.title}</div>
                        <div className="sub-text">{booking.property_info.city}</div>
                      </div>
                    </td>
                    <td>
                      <div className="date-cell">
                        <div>{new Date(booking.check_in).toLocaleDateString()}</div>
                        <div className="sub-text">{t('admin.bookings.to')} {new Date(booking.check_out).toLocaleDateString()}</div>
                      </div>
                    </td>
                    <td>${booking.total_price}</td>
                    <td>
                      <span className={`status-badge ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {booking.status === 'confirmed' && (
                          <button
                            onClick={() => cancelBooking(booking.id)}
                            className="btn-sm btn-danger"
                          >
                            {t('admin.bookings.cancel')}
                          </button>
                        )}
                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateStatus(booking.id, 'confirmed')}
                              className="btn-sm btn-success"
                            >
                              {t('admin.bookings.confirm')}
                            </button>
                            <button
                              onClick={() => cancelBooking(booking.id)}
                              className="btn-sm btn-danger"
                            >
                              {t('admin.bookings.cancel')}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {bookings.length === 0 && (
              <div className="no-results">{t('admin.bookings.noBookings')}</div>
            )}
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="pagination">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                {t('common.previous')}
              </button>
              <span>{t('property.page')} {pagination.page} {t('property.of')} {pagination.pages}</span>
              <button
                disabled={currentPage === pagination.pages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminBookings;
